import { cacheGet, cacheSet, cacheDel, cacheKeyForTenant } from './cache'
import { normalizeSnapshotDates } from './snapshot-dates'

// ─────────────────────────────────────────────────────────────────────────────
// Media Types
// ─────────────────────────────────────────────────────────────────────────────

export type VideoMedia = {
  url: string      // https://cdn.../trips/<id>/videos/<filename>.mp4
  poster: string   // https://cdn.../trips/<id>/videos/<filename>.jpg
  duration?: number  // seconds (ffprobe)
  width?: number
  height?: number
}

// Single ordered gallery item — image (no poster) OR video (with poster).
// Used in the new snapshot `trips[].media` array (replaces images + videos).
export type MediaItem = {
  url: string
  poster?: string    // present → video
  duration?: number
  width?: number
  height?: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier Configuration Types
// ─────────────────────────────────────────────────────────────────────────────

export type TierLevel = 'free' | 'basic' | 'premium' | 'enterprise'

export type FeatureFlags = {
  // Core features
  hidePoweredBy: boolean
  maxTripsDisplayed: number  // -1 = unlimited
  maxPinnedDisplayed: number  // Links-tab highlight tiles; -1 = unlimited
  showGallery: boolean

  // Highlight section visibility
  showWhyBook: boolean
  showQuickFacts: boolean
  showSupport: boolean
  showPickupPoints: boolean
  showDestinations: boolean
  showSafety: boolean
  showCancellation: boolean
  showUpcoming: boolean
  showPayments: boolean
  showUpiPay: boolean
  showLanguages: boolean
  showAwards: boolean
  showDownloads: boolean
  showTrust: boolean
  showFaq: boolean
}

export type HeaderVariant = 'pill' | 'stack' | 'center' | 'chip' | 'left'
export type CardVariant = 'variant-0' | 'variant-1' | 'variant-2' | 'variant-3' | 'variant-4' | 'variant-5'

export type TierLayoutConfig = {
  heroVariant: 'compact' | 'standard' | 'immersive'
  headerVariant: HeaderVariant
  highlightCardVariant: CardVariant
  tripCardVariant: CardVariant
  galleryStyle: 'grid' | 'carousel' | 'masonry'
  footerStyle: 'minimal' | 'standard' | 'premium'
}

export type TierThemeExtensions = {
  gradientOverlay?: string
  accentColor?: string
  cardShadow?: 'none' | 'subtle' | 'elevated'
  borderRadius?: 'sharp' | 'rounded' | 'pill'
}

export type HighlightChip = {
  label: string
  value: string
  icon?: 'clock' | 'users' | 'sparkles' | 'heart' | 'map' | 'star' | 'calendar'
}

export type TierConfig = {
  tier: TierLevel
  features: FeatureFlags
  layout: TierLayoutConfig
  themeExtensions?: TierThemeExtensions
  highlightChips?: HighlightChip[]  // Configurable chips shown below header (max 3)
  expiresAt?: string  // ISO date for subscription expiry
  templateId?: 'classic' | 'modern' | 'luxe'  // Premium template selection (defaults to 'classic')
}

// ─────────────────────────────────────────────────────────────────────────────
// Vendor Snapshot Type
// ─────────────────────────────────────────────────────────────────────────────


export type VendorSnapshot = {
  vendorId: string
  slug: string
  branding: {
    name: string
    logo?: string
    cover?: string
    favicon?: string
    theme?: { primary?: string; secondary?: string }
    /** Publish-time, format-safe copies (JPEG/PNG) for consumers that cannot decode the editor's WebP: /api/og (Satori) + Google favicon. */
    derived?: {
      ogCover?: string
      ogLogo?: string
      favicon?: string
      /** Publish-time share-card JPEGs (<300 KB, WhatsApp-safe): home + per trip id. */
      cards?: { home?: string; trips?: Record<string, string> }
    }
  }
  // Hostnames (apex + www) where this snapshot is authoritative. Populated by
  // TripzOperator's mini-site publisher from MiniSite.customDomains (active).
  customDomains?: string[]
  about?: string
  // Optional vendor-facing fields for UI wiring
  handle?: string
  tagline?: string
  verified?: boolean
  socials?: { website?: string; instagram?: string; youtube?: string; facebook?: string; twitter?: string; email?: string; phone?: string; whatsapp?: string }
  pinnedTripIds?: string[]
  featureTripIds?: string[]
  // Operator toggle: slow Ken Burns drift on cover/tile imagery (off when absent)
  cinematicAnimation?: boolean
  // Tier configuration (optional - defaults to free tier)
  tierConfig?: TierConfig
  trips: Array<{
    id: string
    title: string
    // Pricing/marketing
    priceFrom?: number
    // Plan 75: the trip price shown struck through while an operator offer prices this trip on the mini-site
    listPriceFrom?: number
    rating?: number
    // Media and linking
    image?: string
    // Ordered gallery from the snapshot (canonical). Items without `poster`
    // are images; items with `poster` are videos. Preserves user upload order.
    media?: MediaItem[]
    // Legacy parallel arrays — only present on snapshots published before
    // the unified `media` field rolled out. Kept for backward compat fallback.
    images?: string[]
    videos?: VideoMedia[]
    href?: string
    slug?: string
    tags?: string[]
    // Mini itinerary wiring
    destinations?: string[]
    durationDays?: number
    nextDate?: string // ISO (back-compat)
    nextDates?: string[] // ISO list (new)
    everyDay?: boolean // if true, show "Every day"
    repeatEveryDays?: number // recurrence interval in days
    isWeekendTrip?: boolean
    summary?: string
    highlights?: string[]
    exclusions?: string[]
    startFrom?: string
    itineraryShort: string[]
    inclusions?: string[]
    whatsapp?: string
    itineraryLink?: string
    // Schedule type fields
    scheduleType?: 'fixed' | 'recurring' | 'everyday' | 'on-request'
    recurringDays?: number[]
    // Curated booking/payment-plan exposure (traveler "Book this trip"). Present
    // only when the operator enabled a per-trip payment plan + a per-person price.
    // Stage amounts resolve against (perPersonMinor × pax) on the client.
    booking?: {
      enabled: boolean
      currency: string
      perPersonMinor: number
      /** Plan 75: each room's per-person price (after a live mini-site offer, with its own price as `listPerPersonMinor`). */
      roomSharingPrices?: Array<{ occupancy: number; perPersonMinor: number; listPerPersonMinor?: number }>
      /** Plan 76: choosable pickups and their surcharge over the cheapest, per room. Display only: the quote is charged. */
      pickupOptions?: Array<{
        id: string
        label: string
        time?: string
        surchargeMinor: number
        roomSurcharges?: Array<{ occupancy: number; surchargeMinor: number }>
      }>
      stages: Array<{
        label: string
        kind: 'fixed' | 'percent' | 'remaining'
        amountMinor?: number
        percent?: number
        dueLabel: string
        refundable: boolean
      }>
    }
  }>
  // Premium section content (operator-configured via TripzOperator editor)
  premiumContent?: {
    whyBookItems?: Array<{ title: string; description: string; icon?: string }>
    quickFactItems?: Array<{ label: string; value: string }>
    safetyCertifications?: string[]
    cancellationPolicy?: string
    paymentMethods?: string[]
    languages?: string[]
    awards?: Array<{ title: string; year?: string; issuer?: string }>
    downloads?: Array<{ title: string; url: string; type?: string }>
    trustBadges?: Array<{ label: string; icon?: string }>
    faqItems?: Array<{ question: string; answer: string }>
    supportHours?: string
    avgReplyTime?: string
  }
  // Payment-light (premium opt-in): the operator's own UPI collection details.
  // Money goes DIRECT to the operator — TripzMini only displays the QR / VPA.
  // Only present when the operator enabled payments AND set a UPI ID and/or QR.
  // `gateway` is the Razorpay Route "Pay online" path (money flows through the
  // platform and is split to the operator) — independent of the UPI block;
  // only emitted for a KYC-verified linked account. Frozen contract (§5.2):
  // the snapshot carries NO keys — order-create returns keyId at checkout.
  payments?: {
    upiEnabled?: boolean
    upiId?: string
    qrUrl?: string
    payeeName?: string
    gateway?: {
      provider?: string
      enabled?: boolean
    }
  }
  updatedAt?: string
  version?: number
}

const BASE = import.meta.env.VITE_SNAPSHOT_BASE || '/sample'
const CACHE_TTL_SEC = parseInt(import.meta.env.VITE_CACHE_TTL_SEC || '600', 10)

function ensureTripPriorityLists(snapshot: VendorSnapshot): VendorSnapshot {
  const hasPinned = Array.isArray(snapshot.pinnedTripIds) && snapshot.pinnedTripIds.length > 0
  const hasFeatured = Array.isArray(snapshot.featureTripIds) && snapshot.featureTripIds.length > 0

  // Build full, valid trip ID list
  const allTripIds = (snapshot.trips ?? [])
    .map((trip) => trip.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)

  if (!allTripIds.length) {
    return snapshot
  }

  // Pinned: cap to 4
  const pinnedFallback = allTripIds.slice(0, 4)
  // Featured: include all
  const featuredFallback = allTripIds

  return {
    ...snapshot,
    ...(hasPinned ? {} : { pinnedTripIds: pinnedFallback }),
    ...(hasFeatured ? {} : { featureTripIds: featuredFallback }),
  }
}

/** Everything a fetched snapshot needs before any template reads it. */
function normalizeSnapshot(data: VendorSnapshot): VendorSnapshot {
  return normalizeSnapshotDates(ensureTripPriorityLists(data))
}

export async function fetchSnapshot(tenant: string): Promise<VendorSnapshot> {
  // Check in-memory cache first
  const cacheKey = cacheKeyForTenant(tenant)
  const cached = cacheGet<VendorSnapshot>(cacheKey)
  if (cached) {
    return cached.value
  }

  // Fetch from network
  const snapshot = await fetchSnapshotFromNetwork(tenant)

  // Store in cache
  cacheSet(cacheKey, snapshot, CACHE_TTL_SEC)

  return snapshot
}

/**
 * Invalidate cached snapshot for a tenant
 */
export function invalidateSnapshot(tenant: string): void {
  cacheDel(cacheKeyForTenant(tenant))
}

/**
 * Force refresh snapshot from network (invalidates cache first)
 */
export async function refreshSnapshot(tenant: string): Promise<VendorSnapshot> {
  invalidateSnapshot(tenant)
  return fetchSnapshot(tenant)
}

async function fetchSnapshotFromNetwork(tenant: string): Promise<VendorSnapshot> {
  // Special-case local config for TripzSearch default view
  if (tenant === 'tripzsearch' || tenant == '') {
    const resLocal = await fetch('/tripzsearch.json', { cache: 'no-store' })
    if (!resLocal.ok) throw new Error('TripzSearch config not found')
    const data = await resLocal.json() as VendorSnapshot
    return normalizeSnapshot(data)
  }

  // Try online mock endpoint first
  const online = `${BASE.replace(/\/$/,'')}/${tenant}.json`
  try {
    const res = await fetch(online, { cache: 'no-store' })
    if (res.ok) {
      const data = await res.json() as VendorSnapshot
      return normalizeSnapshot(data)
    }
  } catch {}

  // Fallback to local sample (if BASE points to /sample already this is the same)
  const fallback = `/sample/${tenant}.json`
  const res2 = await fetch(fallback, { cache: 'no-store' })
  if (!res2.ok) throw new Error('Snapshot not found')
  const data = await res2.json() as VendorSnapshot
  return normalizeSnapshot(data)
}
